import { FileDownloadOutlined, FileUploadOutlined, InfoOutlined } from '@mui/icons-material';
import { Box, Button, CircularProgress, Divider, IconButton, Popover, Stack, Tooltip, Typography } from '@mui/material';
import { type FC, useState } from 'react';
import { usePipelineStatusQuery } from 'src/hooks/pipeline-status/usePipelineStatusQuery';
import type { components } from 'src/infrastructure/rest-api/generated';
import { formatPipelineStatusDate } from 'src/utils/formatPipelineStatusDate';

type Entry = components['schemas']['PipelineStatusEntryModel'];
type Response = components['schemas']['PipelineStatusModel'];
type Run = components['schemas']['PipelineRunModel'];

const unavailable = 'Pipeline information is temporarily unavailable.';

const runTime = (run: Run, now: Date): string => {
    const timestamp = run.runEnd ?? run.runStart;
    return timestamp ? formatPipelineStatusDate(timestamp, now) : 'Time unavailable';
};

const runStatus = (run: Run): string => (run.status === 'InProgress' ? 'Running' : run.status);

const activeImportLabel = (run: Run, now: Date): string => {
    if (run.status === 'InProgress') return 'Running';
    if (!run.runStart) return `Import ${runStatus(run)}`;
    return `Import ${runStatus(run)} - started ${formatPipelineStatusDate(run.runStart, now)}`;
};

const previousLabel = (entry: Entry, now: Date, kind: 'import' | 'sync'): string => {
    if (!entry.previousRun) return `No recent ${kind}`;
    const prefix = entry.previousRun.status === 'Succeeded' ? '' : `${runStatus(entry.previousRun)} `;
    return `${prefix}${runTime(entry.previousRun, now)}`;
};

const nextExportLabel = (entry: Entry, now: Date): string => {
    if (entry.currentRun) {
        const run = entry.currentRun;
        if (run.status === 'InProgress') return runStatus(run);
        return run.runStart ? `${runStatus(run)} ${formatPipelineStatusDate(run.runStart, now)}` : runStatus(run);
    }
    if (entry.nextScheduledRun) return formatPipelineStatusDate(entry.nextScheduledRun, now);
    return entry.triggerState === 'Stopped' || entry.triggerState === 'Disabled' ? 'Sync paused' : 'No upcoming sync';
};

const RunningStatusDot: FC<{ label: string }> = ({ label }) => (
    <Box
        component="span"
        role="status"
        aria-label={label}
        sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'success.light',
            boxShadow: '0 0 0 3px rgb(76 175 80 / 20%)',
            flexShrink: 0,
        }}
    />
);

const PipelineStatusDetails: FC<{ data: Response; now: Date }> = ({ data, now }) => (
    <Stack sx={{ gap: 2 }}>
        <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Pipeline activity
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                Last checked {formatPipelineStatusDate(data.retrievedAt, now)}
            </Typography>
        </Box>
        <Divider />
        <Stack direction="row" sx={{ gap: 1.5, alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', color: 'info.main', pt: 0.25 }}>
                <FileDownloadOutlined fontSize="small" />
            </Box>
            <Stack sx={{ gap: 0.75, minWidth: 0 }}>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="subtitle2">Daily import</Typography>
                    <Tooltip title="Daily import is the nightly run that synchronizes Storeforecast with fresh data, such as Sales Orders, Stores, and Styles.">
                        <IconButton
                            size="small"
                            aria-label="About Daily import"
                            sx={{ p: 0.25, color: 'text.secondary' }}
                        >
                            <InfoOutlined sx={{ fontSize: 15 }} />
                        </IconButton>
                    </Tooltip>
                </Stack>
                {data.import.currentRun && (
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                        {data.import.currentRun.status === 'InProgress' && (
                            <RunningStatusDot label="Daily import running" />
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {activeImportLabel(data.import.currentRun, now)}
                        </Typography>
                    </Stack>
                )}
                <Typography variant="body2" color="text.secondary">
                    Last sync:{' '}
                    <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                        {previousLabel(data.import, now, 'import')}
                    </Box>
                </Typography>
            </Stack>
        </Stack>
        <Divider />
        <Stack direction="row" sx={{ gap: 1.5, alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', color: 'warning.main', pt: 0.25 }}>
                <FileUploadOutlined fontSize="small" />
            </Box>
            <Stack sx={{ gap: 0.75, minWidth: 0 }}>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="subtitle2">Sync to Cube</Typography>
                    <Tooltip title="Sync to Cube means that data entered by users in Storeforecast is sent to the Cube.">
                        <IconButton
                            size="small"
                            aria-label="About Sync to Cube"
                            sx={{ p: 0.25, color: 'text.secondary' }}
                        >
                            <InfoOutlined sx={{ fontSize: 15 }} />
                        </IconButton>
                    </Tooltip>
                </Stack>
                {data.export.currentRun && (
                    <Typography variant="body2" color="text.secondary">
                        Current sync:{' '}
                        <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                            {nextExportLabel(data.export, now)}
                        </Box>
                    </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                    Last sync:{' '}
                    <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                        {previousLabel(data.export, now, 'sync')}
                    </Box>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Next sync:{' '}
                    <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                        {data.export.nextScheduledRun
                            ? formatPipelineStatusDate(data.export.nextScheduledRun, now)
                            : data.export.triggerState === 'Stopped' || data.export.triggerState === 'Disabled'
                              ? 'Sync paused'
                              : 'No upcoming sync'}
                    </Box>
                </Typography>
            </Stack>
        </Stack>
    </Stack>
);

export const PipelineStatus: FC = () => {
    const { data, loading, error, refetch } = usePipelineStatusQuery();
    const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
    const [now] = useState(() => new Date());
    const status = data?.import && data.export ? data : undefined;
    const activeImport = status?.import.currentRun;
    const activeExport = status?.export.currentRun;
    const isImportRunning = activeImport?.status === 'InProgress';
    const isExportRunning = activeExport?.status === 'InProgress';
    let importLabel = 'Import loading';
    let exportLabel = 'Sync loading';

    if (!loading) {
        importLabel =
            !error && status
                ? isImportRunning
                    ? 'Daily import running'
                    : `Last Import ${previousLabel(status.import, now, 'import')}`
                : 'Import unavailable';
        exportLabel =
            !error && status
                ? isExportRunning
                    ? 'Sync running'
                    : `Next sync ${nextExportLabel(status.export, now)}`
                : 'Sync unavailable';
    }
    const buttonLabel = [
        activeImport && !isImportRunning && activeImportLabel(activeImport, now),
        importLabel,
        exportLabel,
    ]
        .filter(Boolean)
        .join('; ');

    return (
        <>
            <Tooltip title="Daily import and Cube sync details">
                <Button
                    variant="text"
                    onClick={(event) => {
                        setAnchorElement(event.currentTarget);
                        if (!loading) void refetch();
                    }}
                    aria-label={`${buttonLabel}. Show pipeline details`}
                    sx={{ color: 'white', textAlign: 'left', textTransform: 'none', maxWidth: { xs: 160, sm: 210 } }}
                >
                    <Stack sx={{ alignItems: 'flex-start', minWidth: 0, gap: 0.25 }}>
                        {activeImport && !isImportRunning && (
                            <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
                                <Typography
                                    variant="caption"
                                    sx={{ lineHeight: 1.3, overflowWrap: 'anywhere', fontWeight: 700 }}
                                >
                                    {activeImportLabel(activeImport, now)}
                                </Typography>
                            </Stack>
                        )}
                        <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
                            <FileDownloadOutlined sx={{ fontSize: 16, flexShrink: 0, color: 'info.light' }} />
                            <Typography variant="caption" sx={{ lineHeight: 1.3, overflowWrap: 'anywhere' }}>
                                {importLabel}
                            </Typography>
                            {isImportRunning && <RunningStatusDot label="Daily import running" />}
                        </Stack>
                        <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
                            <FileUploadOutlined sx={{ fontSize: 16, flexShrink: 0, color: 'warning.light' }} />
                            <Typography variant="caption" sx={{ lineHeight: 1.3, overflowWrap: 'anywhere' }}>
                                {exportLabel}
                            </Typography>
                            {isExportRunning && <RunningStatusDot label="Sync running" />}
                        </Stack>
                    </Stack>
                </Button>
            </Tooltip>
            <Popover
                open={Boolean(anchorElement)}
                anchorEl={anchorElement}
                onClose={() => setAnchorElement(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { p: 2.5, width: 300, maxWidth: 'calc(100vw - 24px)' } } }}
            >
                {loading ? (
                    <CircularProgress size={20} aria-label="Loading pipeline status" />
                ) : error || !status ? (
                    <Typography color="text.secondary">{unavailable}</Typography>
                ) : (
                    <PipelineStatusDetails data={status} now={now} />
                )}
            </Popover>
        </>
    );
};
